import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import cl from './Style.module.css';
import { useLoader } from '../../hooks/useLoader';
import AdminService from '../../API/AdminService';
import logger from '../../utils/logger';
import {
    IconArrowLeft, IconUser, IconBook, IconEvents, IconPays,
} from '../../components/Icons';

const Student = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [student, setStudent] = useState({
        ferstName: '',
        lastName: '',
        age: '',
        nameRoditel: '',
        phoneRod: '',
        tg: '',
        comment: '',
        status: 'Активен',
        group: '[]',
        events: '[]'  // Добавляем поле для мероприятий
    });

    const [pays, setPays] = useState([]);
    const [courses, setCourses] = useState([]);
    const [events, setEvents] = useState([]);  // Добавляем состояние для мероприятий
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showAddCourseModal, setShowAddCourseModal] = useState(false);
    const [showRemoveCourseModal, setShowRemoveCourseModal] = useState(false);

    // Добавляем состояния для модалок мероприятий
    const [showAddEventModal, setShowAddEventModal] = useState(false);
    const [showRemoveEventModal, setShowRemoveEventModal] = useState(false);

    const [notification, setNotification] = useState('');

    // Загрузка студента по ID
    const [fetchStudent, isLoading, error] = useLoader(async () => {
        try {
            const response = await AdminService.getStudent(id);
            if (response.data) {
                setStudent(response.data);
            }
        } catch (error) {
            logger.error('Ошибка загрузки ученика:', error);
        }
    });

    const [fetchPays, isLoadingPays] = useLoader(async () => {
        try {
            const response = await AdminService.getAllPays();
            if (response.data) {
                setPays(response.data);
            }
        } catch (error) {
            logger.error('Ошибка загрузки платежей:', error);
        }
    });

    // Загрузка всех курсов
    const [fetchCourses, isLoadingCourses] = useLoader(async () => {
        try {
            const response = await AdminService.getAllCourses();
            if (response.data) {
                setCourses(response.data);
            }
        } catch (error) {
            logger.error('Ошибка загрузки курсов:', error);
        }
    });

    // Загрузка всех мероприятий
    const [fetchEvents, isLoadingEvents] = useLoader(async () => {
        try {
            const response = await AdminService.getAllEvents();
            if (response.data) {
                setEvents(response.data);
            }
        } catch (error) {
            logger.error('Ошибка загрузки мероприятий:', error);
        }
    });

    // Обновление данных студента
    const [updateStudent, isUpdating] = useLoader(async () => {
        try {
            const response = await AdminService.updateStudent(id, student);
            if (response.data) {
                setStudent(response.data);
                setNotification('Данные ученика успешно обновлены!');
                setTimeout(() => setNotification(''), 3000);
            }
        } catch (error) {
            logger.error('Ошибка обновления ученика:', error);
        }
    });

    // Удаление студента
    const [deleteStudent, isDeleting] = useLoader(async () => {
        try {
            await AdminService.deleteStudent(id);
            setNotification('Ученик удален');
            setTimeout(() => {
                navigate('/main/students');
            }, 2000);
        } catch (error) {
            logger.error('Ошибка удаления ученика:', error);
        }
    });

    // Загрузка данных при монтировании
    useEffect(() => {
        if (id) {
            fetchStudent();
            fetchCourses();
            fetchEvents();  // Добавляем загрузку мероприятий
            fetchPays(); // загрузка платежей
        }
    }, [id]);

    // Получить список ID курсов студента
    const getStudentCourseIds = () => {
        try {
            const parsed = JSON.parse(student.group || '[]');
            return Array.isArray(parsed) ? parsed : [];
        } catch (error) {
            logger.error('Ошибка парсинга курсов:', error);
            return [];
        }
    };

    const getStudentPays = () => {
        return pays.filter(pay => pay.idStudent === parseInt(id));
    };

    const getStudentPaysSorted = () => {
        return getStudentPays().sort((a, b) => new Date(b.date) - new Date(a.date));
    };

    const getTotalPaysSum = () => {
        return getStudentPays().reduce((sum, pay) => sum + (pay.price || 0), 0);
    };

    // Получить курсы студента
    const getStudentCourses = () => {
        const courseIds = getStudentCourseIds();
        return courses.filter(course => courseIds.includes(course.id));
    };

    // Получить доступные курсы для записи
    const getAvailableCourses = () => {
        const courseIds = getStudentCourseIds();
        return courses.filter(course => !courseIds.includes(course.id));
    };

    // ИСПРАВЛЕННЫЕ ФУНКЦИИ ДЛЯ РАБОТЫ С МЕРОПРИЯТИЯМИ
    // Получить список ID мероприятий студента - ИСПРАВЛЕНО
    const getStudentEventIds = () => {
        try {
            const parsed = JSON.parse(student.events || '[]');
            // Проверяем, что результат парсинга - это массив
            return Array.isArray(parsed) ? parsed : [];
        } catch (error) {
            logger.error('Ошибка парсинга мероприятий:', error);
            return [];
        }
    };

    // Получить мероприятия студента - теперь работает корректно
    const getStudentEvents = () => {
        const eventIds = getStudentEventIds();
        return events.filter(event => eventIds.includes(event.id));
    };

    // Получить доступные мероприятия для записи
    const getAvailableEvents = () => {
        const eventIds = getStudentEventIds();
        return events.filter(event => !eventIds.includes(event.id));
    };

    // Обработка изменений в форме
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setStudent(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Записать на курс
    const handleAddCourse = (courseId) => {
        const currentCourseIds = getStudentCourseIds();
        if (!currentCourseIds.includes(courseId)) {
            const newCourseIds = [...currentCourseIds, courseId];
            const updatedStudent = {
                ...student,
                group: JSON.stringify(newCourseIds)
            };
            setStudent(updatedStudent);
            updateStudentCourses(updatedStudent);
        }
        setShowAddCourseModal(false);
    };

    // Выписать из курса
    const handleRemoveCourse = (courseId) => {
        const currentCourseIds = getStudentCourseIds();
        const newCourseIds = currentCourseIds.filter(id => id !== courseId);
        const updatedStudent = {
            ...student,
            group: JSON.stringify(newCourseIds)
        };
        setStudent(updatedStudent);
        updateStudentCourses(updatedStudent);
        setShowRemoveCourseModal(false);
    };

    // Записать на мероприятие
    const handleAddEvent = (eventId) => {
        const currentEventIds = getStudentEventIds();
        if (!currentEventIds.includes(eventId)) {
            const newEventIds = [...currentEventIds, eventId];
            const updatedStudent = {
                ...student,
                events: JSON.stringify(newEventIds)
            };
            setStudent(updatedStudent);
            updateStudentEvents(updatedStudent);
        }
        setShowAddEventModal(false);
    };

    // Выписать с мероприятия
    const handleRemoveEvent = (eventId) => {
        const currentEventIds = getStudentEventIds();
        const newEventIds = currentEventIds.filter(id => id !== eventId);
        const updatedStudent = {
            ...student,
            events: JSON.stringify(newEventIds)
        };
        setStudent(updatedStudent);
        updateStudentEvents(updatedStudent);
        setShowRemoveEventModal(false);
    };

    // Обновить курсы студента на сервере
    const updateStudentCourses = async (updatedStudent) => {
        try {
            const response = await AdminService.updateStudent(id, updatedStudent);
            if (response.data) {
                setStudent(response.data);
                setNotification('Курсы ученика обновлены!');
                setTimeout(() => setNotification(''), 3000);
            }
        } catch (error) {
            logger.error('Ошибка обновления курсов ученика:', error);
        }
    };

    // Обновить мероприятия студента на сервере
    const updateStudentEvents = async (updatedStudent) => {
        try {
            const response = await AdminService.updateStudent(id, updatedStudent);
            if (response.data) {
                setStudent(response.data);
                setNotification('Мероприятия ученика обновлены!');
                setTimeout(() => setNotification(''), 3000);
            }
        } catch (error) {
            logger.error('Ошибка обновления мероприятий ученика:', error);
        }
    };

    // Сохранение изменений
    const handleSave = (e) => {
        e.preventDefault();
        if (!student.ferstName || !student.lastName) {
            alert('Пожалуйста, заполните обязательные поля: Имя и Фамилия');
            return;
        }
        updateStudent();
    };

    // Подтверждение удаления
    const handleDeleteConfirm = () => {
        deleteStudent();
        setShowDeleteModal(false);
    };

    // Возврат к списку студентов
    const handleBack = () => {
        navigate('/main/students');
    };

    if (isLoading) {
        return (
            <div className={cl.page_wrapper}>
                <div className={cl.wrap_content_1}>
                    <button className={cl.backLink} onClick={handleBack}>
                        <IconArrowLeft width={14} height={14}/>
                        <span>К ученикам</span>
                    </button>
                    <div className={cl.noData}>Загрузка данных ученика…</div>
                </div>
            </div>
        );
    }

    // Класс пилла статуса в зависимости от значения
    const statusKey = (student.status || '').toLowerCase();
    const statusPillClass =
        statusKey === 'активен' ? cl.pageHeaderPillActive :
        statusKey === 'пробный' ? cl.pageHeaderPill :
        cl.pageHeaderPill;

    return (
        <div className={cl.page_wrapper}>
            <div className={cl.wrap_content_1}>
                {/* Уведомления */}
                {notification && (
                    <div className={cl.notification}>
                        {notification}
                    </div>
                )}

                {/* Назад */}
                <button className={cl.backLink} onClick={handleBack}>
                    <IconArrowLeft width={14} height={14}/>
                    <span>К ученикам</span>
                </button>

                {/* Карточка-шапка ученика */}
                <div className={cl.pageHeader}>
                    <div className={cl.pageHeaderLeft}>
                        <div className={cl.pageHeaderIcon}>
                            <IconUser width={22} height={22}/>
                        </div>
                        <div className={cl.pageHeaderText}>
                            <div className={cl.pageHeaderTopRow}>
                                <h1 className={cl.pageHeaderTitle}>
                                    {student.ferstName} {student.lastName}
                                </h1>
                                {student.status && (
                                    <span className={statusPillClass}>
                                        {statusKey === 'активен' && <span className={cl.pageHeaderPillDot}/>}
                                        <span className={cl.pageHeaderPillStrong}>{student.status}</span>
                                    </span>
                                )}
                                {student.age && (
                                    <span className={cl.pageHeaderPill}>
                                        <span className={cl.pageHeaderPillStrong}>{student.age}</span>
                                        <span className={cl.pageHeaderPillMuted}>лет</span>
                                    </span>
                                )}
                            </div>
                            <div className={cl.breadcrumbs}>
                                АдминПанель · Ученики · Карточка
                            </div>
                        </div>
                    </div>
                </div>

                <div className={cl.studentLayout}>
                    {/* Левая колонка - Личные данные */}
                    <div className={cl.personalDataCard}>
                        <h2>
                            <span className={cl.sectionCardTitleIcon}>
                                <IconUser width={14} height={14}/>
                            </span>
                            Личные данные
                        </h2>
                        <form onSubmit={handleSave} className={cl.studentForm}>
                            <div className={cl.formGrid}>
                                <div className={cl.formColumn}>
                                    <div className={cl.formGroup}>
                                        <label>Имя *</label>
                                        <input
                                            type="text"
                                            name="ferstName"
                                            value={student.ferstName}
                                            onChange={handleInputChange}
                                            className={cl.formInput}
                                            required
                                        />
                                    </div>

                                    <div className={cl.formGroup}>
                                        <label>Возраст</label>
                                        <input
                                            type="number"
                                            name="age"
                                            value={student.age}
                                            onChange={handleInputChange}
                                            className={cl.formInput}
                                            min="1"
                                            max="100"
                                        />
                                    </div>

                                    <div className={cl.formGroup}>
                                        <label>Телефон родителя</label>
                                        <input
                                            type="tel"
                                            name="phoneRod"
                                            value={student.phoneRod}
                                            onChange={handleInputChange}
                                            className={cl.formInput}
                                        />
                                    </div>
                                </div>

                                <div className={cl.formColumn}>
                                    <div className={cl.formGroup}>
                                        <label>Фамилия *</label>
                                        <input
                                            type="text"
                                            name="lastName"
                                            value={student.lastName}
                                            onChange={handleInputChange}
                                            className={cl.formInput}
                                            required
                                        />
                                    </div>

                                    <div className={cl.formGroup}>
                                        <label>Имя родителя</label>
                                        <input
                                            type="text"
                                            name="nameRoditel"
                                            value={student.nameRoditel}
                                            onChange={handleInputChange}
                                            className={cl.formInput}
                                        />
                                    </div>

                                    <div className={cl.formGroup}>
                                        <label>Telegram</label>
                                        <input
                                            type="text"
                                            name="tg"
                                            value={student.tg}
                                            onChange={handleInputChange}
                                            className={cl.formInput}
                                            placeholder="@username"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className={cl.formGroup}>
                                <label>Статус</label>
                                <select
                                    name="status"
                                    value={student.status}
                                    onChange={handleInputChange}
                                    className={cl.formInput}
                                >
                                    <option value="Активен">Активен</option>
                                    <option value="Пробный">Пробный</option>
                                    <option value="Архив">Архив</option>
                                </select>
                            </div>

                            <div className={cl.formGroup}>
                                <label>Комментарий</label>
                                <textarea
                                    name="comment"
                                    value={student.comment}
                                    onChange={handleInputChange}
                                    className={cl.formTextarea}
                                    rows="3"
                                />
                            </div>

                            <div className={cl.formActions}>
                                <button
                                    type="button"
                                    onClick={() => setShowDeleteModal(true)}
                                    className={cl.deleteButton}
                                    disabled={isDeleting}
                                >
                                    {isDeleting ? 'Удаление...' : 'Удалить ученика'}
                                </button>
                                <button
                                    type="submit"
                                    className={cl.saveButton}
                                    disabled={isUpdating}
                                >
                                    {isUpdating ? 'Сохранение...' : 'Сохранить изменения'}
                                </button>
                            </div>
                        </form>
                    </div>

                    {/* Записанные курсы */}
                    <div className={cl.coursesCard}>
                        <h2>
                            <span className={cl.sectionCardTitleIcon}>
                                <IconBook width={14} height={14}/>
                            </span>
                            Записанные курсы
                        </h2>

                        <div className={cl.coursesList}>
                            {getStudentCourses().length === 0 ? (
                                <div className={cl.noCoursesMessage}>
                                    Ученик не записан на курсы
                                </div>
                            ) : (
                                getStudentCourses().map(course => (
                                    <div key={course.id} className={cl.courseItem}>
                                        <div className={cl.courseInfo}>
                                            <h4>{course.name}</h4>
                                            <p><strong>Возраст:</strong> {course.ageRange || 'Не указан'}</p>
                                            <p><strong>День:</strong> {
                                                course.day === 0 ? 'Воскресенье' :
                                                    course.day === 1 ? 'Понедельник' :
                                                        course.day === 2 ? 'Вторник' :
                                                            course.day === 3 ? 'Среда' :
                                                                course.day === 4 ? 'Четверг' :
                                                                    course.day === 5 ? 'Пятница' :
                                                                        course.day === 6 ? 'Суббота' : 'Не указан'
                                            }</p>
                                            <p><strong>Время:</strong> {course.timeStart || 'Не указано'} - {course.timeEnd || 'Не указано'}</p>
                                            <p><strong>Преподаватель:</strong> {course.prepod || 'Не указан'}</p>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>

                        <div className={cl.courseActions}>
                            <button
                                className={cl.addCourseButton}
                                onClick={() => setShowAddCourseModal(true)}
                            >
                                Записать на курс
                            </button>
                            <button
                                className={cl.removeCourseButton}
                                onClick={() => setShowRemoveCourseModal(true)}
                                disabled={getStudentCourses().length === 0}
                            >
                                Выписать с курса
                            </button>
                        </div>
                    </div>

                    {/* Записанные мероприятия - используем те же классы что и для курсов */}
                    <div className={cl.coursesCard} >
                        <h2>
                            <span className={cl.sectionCardTitleIcon}>
                                <IconEvents width={14} height={14}/>
                            </span>
                            Записанные мероприятия
                        </h2>

                        <div className={cl.coursesList}>
                            {getStudentEvents().length === 0 ? (
                                <div className={cl.noCoursesMessage}>
                                    Ученик не записан на мероприятия
                                </div>
                            ) : (
                                getStudentEvents().map(event => (
                                    <div key={event.id} className={cl.courseItem}>
                                        <div className={cl.courseInfo}>
                                            <h4>{event.name}</h4>
                                            <p><strong>День:</strong> {
                                                event.day === 0 ? 'Воскресенье' :
                                                    event.day === 1 ? 'Понедельник' :
                                                        event.day === 2 ? 'Вторник' :
                                                            event.day === 3 ? 'Среда' :
                                                                event.day === 4 ? 'Четверг' :
                                                                    event.day === 5 ? 'Пятница' :
                                                                        event.day === 6 ? 'Суббота' : 'Не указан'
                                            }</p>
                                            <p><strong>Время:</strong> {event.timeStart || 'Не указано'} - {event.timeEnd || 'Не указано'}</p>
                                            <p><strong>Даты:</strong> {event.dateStart || 'Не указана'} - {event.dateEnd || 'Не указана'}</p>
                                            <p><strong>Преподаватель:</strong> {event.prepod || 'Не указан'}</p>
                                            <p><strong>Статус:</strong> {event.status || 'Не указан'}</p>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>

                        <div className={cl.courseActions}>
                            <button
                                className={cl.addCourseButton}
                                onClick={() => setShowAddEventModal(true)}
                            >
                                Записать на мероприятие
                            </button>
                            <button
                                className={cl.removeCourseButton}
                                onClick={() => setShowRemoveEventModal(true)}
                                disabled={getStudentEvents().length === 0}
                            >
                                Выписать с мероприятия
                            </button>
                        </div>
                    </div>

                    <div className={cl.coursesCard}>
                        <h2 className={cl.cardTitle}>
                            <span className={cl.sectionCardTitleIcon}>
                                <IconPays width={14} height={14}/>
                            </span>
                            Платежи
                        </h2>

                        {isLoadingPays ? (
                            <p>Загрузка платежей...</p>
                        ) : getStudentPays().length === 0 ? (
                            <p>У ученика пока нет платежей.</p>
                        ) : (
                            <>
                                <div className={cl.payTableContainer}>
                                    <table className={cl.payTable}>
                                        <thead>
                                            <tr>
                                                <th>Дата</th>
                                                <th>Сумма</th>
                                                <th>Тип</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {getStudentPaysSorted().map(pay => (
                                                <tr key={pay.id}>
                                                    <td data-label="Дата">{new Date(pay.date).toLocaleDateString('ru-RU')}</td>
                                                    <td data-label="Сумма">{pay.price} ₽</td>
                                                    <td data-label="Тип">
                                                        <span className={pay.typePay === 'Наличные' ? cl.payCash : cl.payTransfer}>
                                                            {pay.typePay}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <div className={cl.payTotal}>Всего оплачено: {getTotalPaysSum()} ₽</div>
                            </>
                        )}
                    </div>


                </div>

                {/* Модалка добавления курса */}
                {showAddCourseModal && (
                    <div className={cl.modalOverlay}>
                        <div className={cl.modal}>
                            <div className={cl.modalHeader}>
                                <h2>Записать на курс</h2>
                                <button
                                    className={cl.modalClose}
                                    onClick={() => setShowAddCourseModal(false)}
                                >
                                    ×
                                </button>
                            </div>
                            <div className={cl.modalBody}>
                                {getAvailableCourses().length === 0 ? (
                                    <p>Нет доступных курсов для записи</p>
                                ) : (
                                    <div className={cl.courseModalList}>
                                        {getAvailableCourses().map(course => (
                                            <div
                                                key={course.id}
                                                className={cl.courseModalItem}
                                                onClick={() => handleAddCourse(course.id)}
                                            >
                                                <h4>{course.name}</h4>
                                                <p><strong>Возраст:</strong> {course.ageRange || 'Не указан'}</p>
                                                <p><strong>День:</strong> {
                                                    course.day === 0 ? 'Воскресенье' :
                                                        course.day === 1 ? 'Понедельник' :
                                                            course.day === 2 ? 'Вторник' :
                                                                course.day === 3 ? 'Среда' :
                                                                    course.day === 4 ? 'Четверг' :
                                                                        course.day === 5 ? 'Пятница' :
                                                                            course.day === 6 ? 'Суббота' : 'Не указан'
                                                }</p>
                                                <p><strong>Время:</strong> {course.timeStart || 'Не указано'} - {course.timeEnd || 'Не указано'}</p>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Модалка удаления курса */}
                {showRemoveCourseModal && (
                    <div className={cl.modalOverlay}>
                        <div className={cl.modal}>
                            <div className={cl.modalHeader}>
                                <h2>Выписать с курса</h2>
                                <button
                                    className={cl.modalClose}
                                    onClick={() => setShowRemoveCourseModal(false)}
                                >
                                    ×
                                </button>
                            </div>
                            <div className={cl.modalBody}>
                                <div className={cl.courseModalList}>
                                    {getStudentCourses().map(course => (
                                        <div
                                            key={course.id}
                                            className={cl.courseModalItem}
                                            onClick={() => handleRemoveCourse(course.id)}
                                        >
                                            <h4>{course.name}</h4>
                                            <p><strong>Возраст:</strong> {course.ageRange || 'Не указан'}</p>
                                            <p><strong>Время:</strong> {course.timeStart || 'Не указано'} - {course.timeEnd || 'Не указано'}</p>
                                            <p><strong>Преподаватель:</strong> {course.prepod || 'Не указан'}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Модалка добавления мероприятия */}
                {showAddEventModal && (
                    <div className={cl.modalOverlay}>
                        <div className={cl.modal}>
                            <div className={cl.modalHeader}>
                                <h2>Записать на мероприятие</h2>
                                <button
                                    className={cl.modalClose}
                                    onClick={() => setShowAddEventModal(false)}
                                >
                                    ×
                                </button>
                            </div>
                            <div className={cl.modalBody}>
                                {getAvailableEvents().length === 0 ? (
                                    <p>Нет доступных мероприятий для записи</p>
                                ) : (
                                    <div className={cl.courseModalList}>
                                        {getAvailableEvents().map(event => (
                                            <div
                                                key={event.id}
                                                className={cl.courseModalItem}
                                                onClick={() => handleAddEvent(event.id)}
                                            >
                                                <h4>{event.name}</h4>
                                                <p><strong>День:</strong> {
                                                    event.day === 0 ? 'Воскресенье' :
                                                        event.day === 1 ? 'Понедельник' :
                                                            event.day === 2 ? 'Вторник' :
                                                                event.day === 3 ? 'Среда' :
                                                                    event.day === 4 ? 'Четверг' :
                                                                        event.day === 5 ? 'Пятница' :
                                                                            event.day === 6 ? 'Суббота' : 'Не указан'
                                                }</p>
                                                <p><strong>Время:</strong> {event.timeStart || 'Не указано'} - {event.timeEnd || 'Не указано'}</p>
                                                <p><strong>Даты:</strong> {event.dateStart || 'Не указана'} - {event.dateEnd || 'Не указана'}</p>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Модалка удаления мероприятия */}
                {showRemoveEventModal && (
                    <div className={cl.modalOverlay}>
                        <div className={cl.modal}>
                            <div className={cl.modalHeader}>
                                <h2>Выписать с мероприятия</h2>
                                <button
                                    className={cl.modalClose}
                                    onClick={() => setShowRemoveEventModal(false)}
                                >
                                    ×
                                </button>
                            </div>
                            <div className={cl.modalBody}>
                                <div className={cl.courseModalList}>
                                    {getStudentEvents().map(event => (
                                        <div
                                            key={event.id}
                                            className={cl.courseModalItem}
                                            onClick={() => handleRemoveEvent(event.id)}
                                        >
                                            <h4>{event.name}</h4>
                                            <p><strong>День:</strong> {
                                                event.day === 0 ? 'Воскресенье' :
                                                    event.day === 1 ? 'Понедельник' :
                                                        event.day === 2 ? 'Вторник' :
                                                            event.day === 3 ? 'Среда' :
                                                                event.day === 4 ? 'Четверг' :
                                                                    event.day === 5 ? 'Пятница' :
                                                                        event.day === 6 ? 'Суббота' : 'Не указан'
                                            }</p>
                                            <p><strong>Время:</strong> {event.timeStart || 'Не указано'} - {event.timeEnd || 'Не указано'}</p>
                                            <p><strong>Преподаватель:</strong> {event.prepod || 'Не указан'}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Модалка подтверждения удаления */}
                {showDeleteModal && (
                    <div className={cl.modalOverlay}>
                        <div className={cl.deleteModal}>
                            <div className={cl.deleteModalHeader}>
                                <h3>Подтверждение удаления</h3>
                            </div>
                            <div className={cl.deleteModalBody}>
                                <p>Вы точно хотите удалить ученика?</p>
                                <p><strong>{student.ferstName} {student.lastName}</strong></p>
                                <p className={cl.deleteWarning}>Данные восстановить будет нельзя.</p>
                            </div>
                            <div className={cl.deleteModalActions}>
                                <button
                                    className={cl.buttonSecondary}
                                    onClick={() => setShowDeleteModal(false)}
                                >
                                    Отмена
                                </button>
                                <button
                                    className={cl.buttonDanger}
                                    onClick={handleDeleteConfirm}
                                    disabled={isDeleting}
                                >
                                    {isDeleting ? 'Удаление...' : 'Да, удалить'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Student;